// ============================================================================
// OccupancyChartReportScreen — operational reports (July 2026)
// ============================================================================
// "Who is in the house tonight?" — every occupied room on a single chart date,
// with a management summary block (occupancy %, gender mix, resident/foreigner
// split, food-plan mix, ARR / RevPAR / RevPOR). Modelled on the legacy PMS
// occupancy chart but rebuilt to CRS UI standards.
//
// Occupied-on-date rule (matches the backend CTE): checked in on/before the
// chart date AND (actual, else planned) check-out AFTER it — half-open dates.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import { useReportList, useReportSummary } from 'hooks/api/useReport';
import { reportService } from 'api/services/reportService';
import { propertyService } from 'api/services/propertyService';
import notificationService from 'services/notificationService';
import { blobErrorMessage, reserveTab, saveReportFile, showPdf, type ReportFormat } from 'utils/reportFile';
import { formatCurrency } from 'utils/currencyFormat';
import { settlementModeLabel } from 'api/types/settlement.types';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import type {
  ReportListRequest,
  ReportFilter,
  OccupancyChartRow,
  OccupancyChartSummary,
} from 'api/types';
import 'components/modules/dashboard-module/dashboard.css';

const todayIso = () => {
  const d = new Date();
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
};

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const formatDateTime = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      })
    : '-';

interface OccupancyChartReportScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — {'view','export'}. Fail-closed: [] renders no export/print. */
  allowedActions?: string[];
}

const OccupancyChartReportScreen: React.FC<OccupancyChartReportScreenProps> = ({
  tabId,
  allowedActions = [],
}) => {
  // ── Filter state ──────────────────────────────────────────────────────────
  const [propertyId, setPropertyId] = useState<string>('');
  // PMS slice 5: the desk opens it daily — start on the user's default property.
  const { defaultPropertyId } = useDefaultProperty();
  React.useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const [chartDate, setChartDate]   = useState<string>(todayIso());
  // Also list the rooms due out that day (the morning view); figures stay the night's.
  const [includeDepartures, setIncludeDepartures] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  // Property label lookup for the print header (filled by the async loader).
  const propertyLabelsRef = useRef<Record<string, string>>({});
  const fetchedPropertyLabel = usePropertyLabel(propertyId);

  // ── Pagination ────────────────────────────────────────────────────────────
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const enabled = !!propertyId && !!chartDate;

  React.useEffect(() => { setPage(1); }, [propertyId, chartDate, includeDepartures]);

  // Single-date report: the backend reads `from` as the chart date; keep
  // to = from so the shared filter validation passes.
  const listRequest: ReportListRequest = useMemo(() => ({
    propertyId, from: chartDate, to: chartDate, page, pageSize, includeDepartures,
  } as ReportListRequest), [propertyId, chartDate, page, pageSize, includeDepartures]);

  const summaryFilter: ReportFilter = useMemo(() => ({
    propertyId, from: chartDate, to: chartDate,
  }), [propertyId, chartDate]);

  const { data: rawData, pagination, loading, error, refetch } = useReportList<OccupancyChartRow>(
    (req) => reportService.getOccupancyChart(req),
    listRequest,
    enabled,
  );
  const data = useMemo(
    () => rawData.map((r, i) => ({ ...r, id: `${r.roomNumber}-${r.bookingId}-${i}` })),
    [rawData],
  );
  const { data: summary, refetch: refetchSummary } = useReportSummary<OccupancyChartSummary>(
    (f) => reportService.getOccupancyChartSummary(f as ReportFilter),
    summaryFilter,
    enabled,
  );

  const appliedFilterCount = useMemo(() => {
    let n = 0;
    if (propertyId) n++;
    if (chartDate !== todayIso()) n++;
    return n;
  }, [propertyId, chartDate]);

  const handlePageChange     = useCallback((idx: number) => setPage(idx + 1), []);
  const handlePageSizeChange = useCallback((size: number) => { setPageSize(size); setPage(1); }, []);

  const loadProperties = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 500, includeInactive: false });
    const opts = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    opts.forEach((o: SelectOption) => { propertyLabelsRef.current[o.value as string] = o.label; });
    return opts;
  }, []);

  const handleClearFilters = useCallback(() => {
    setChartDate(todayIso());
    // Property is intentionally NOT reset — every report needs a property scope.
  }, []);

  // ── Export / Print — formatted files built by the API (the Fairbooks way) ──
  // Excel downloads; the PDF opens in a new tab for the browser to print.
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const exportFile = useCallback(async (format: ReportFormat) => {
    if (!enabled) {
      notificationService.toast.warning('Select a property first.');
      return;
    }
    const tab = format === 'pdf' ? reserveTab() : null;
    (format === 'pdf' ? setPrinting : setExporting)(true);
    try {
      const { blob, disposition } = await reportService.exportOccupancyChart(
        { ...summaryFilter, includeDepartures }, format);
      if (format === 'pdf') showPdf(blob, tab);
      else saveReportFile(blob, disposition, `RoomOccupancy_${chartDate}.xlsx`);
    } catch (err) {
      tab?.close();
      notificationService.toast.error(await blobErrorMessage(err, format === 'pdf' ? 'Print failed.' : 'Export failed.'));
    } finally {
      (format === 'pdf' ? setPrinting : setExporting)(false);
    }
  }, [enabled, summaryFilter, includeDepartures, chartDate]);
  const handleExport = useCallback(() => { void exportFile('xlsx'); }, [exportFile]);
  const handlePrint = useCallback(() => { void exportFile('pdf'); }, [exportFile]);

  // ── Columns ───────────────────────────────────────────────────────────────
  type Row = OccupancyChartRow & { id: string };
  const columns: SyutGridColumn<Row>[] = useMemo(() => [
    { id: 'roomNumber', header: 'Room', accessorKey: 'roomNumber', size: 110, enableSorting: true,
      cell: ({ row }) => (
        <span style={{ fontStyle: row.original.isDueOut ? 'italic' : undefined }}>
          {row.original.roomNumber}
          {row.original.isDueOut && <span className="badge badge-warning" style={{ fontSize: '0.6rem', marginLeft: '0.35rem' }} title="Due out on the chart date">Due out</span>}
        </span>
      ) },
    { id: 'roomCategoryName', header: 'Category', accessorKey: 'roomCategoryName', size: 160, enableSorting: true,
      cell: ({ row }) => {
        const alloc = row.original.roomCategoryName ?? '-';
        const booked = row.original.bookedCategoryName;
        return booked && booked !== alloc ? (
          <span>
            {alloc}{' '}
            <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>(booked {booked})</span>
          </span>
        ) : <span>{alloc}</span>;
      },
    },
    { id: 'bookingNumber', header: 'Booking #', accessorKey: 'bookingNumber', size: 130, enableSorting: true },
    { id: 'guestFullName', header: 'Guest', accessorKey: 'guestFullName', enableSorting: true,
      cell: ({ getValue }) => (getValue() as string) || '-' },
    { id: 'partyName', header: 'Party / Source', accessorKey: 'partyName', size: 180,
      cell: ({ row }) => (
        <span>
          <span style={{ color: 'var(--text-muted)' }}>{row.original.bookingSource ?? ''}</span>
          {row.original.partyName ? ` — ${row.original.partyName}` : ''}
        </span>
      ),
    },
    { id: 'mealPlanCode', header: 'Plan', accessorKey: 'mealPlanCode', size: 70,
      cell: ({ getValue }) => (getValue() as string) || '-' },
    { id: 'pax', header: 'Pax', accessorKey: 'pax', size: 60 },
    { id: 'nationality', header: 'Nationality', accessorKey: 'nationality', size: 110,
      cell: ({ getValue }) => (getValue() as string) || '-' },
    { id: 'checkInAt', header: 'Check-In', accessorKey: 'checkInAt', size: 130, enableSorting: true,
      cell: ({ getValue }) => formatDateTime(getValue() as string) },
    { id: 'expectedCheckOut', header: 'Exp. C/O', accessorKey: 'expectedCheckOut', size: 105, enableSorting: true,
      cell: ({ getValue }) => formatDate(getValue() as string) },
    { id: 'settlementMode', header: 'Settlement', accessorKey: 'settlementMode', size: 150,
      cell: ({ getValue }) => settlementModeLabel(getValue() as string) || 'Guest pays' },
    { id: 'isComplimentary', header: 'Comp', accessorKey: 'isComplimentary', size: 70,
      cell: ({ getValue }) => (getValue() ? (
        <span style={{
          fontSize: '0.7rem', fontWeight: 600, padding: '0.1rem 0.4rem', borderRadius: '4px',
          background: 'rgba(245, 158, 11, 0.15)', color: '#b45309',
        }}>COMP</span>
      ) : null) },
  ], []);

  // ── ModuleHeader actions ──────────────────────────────────────────────────
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={16} />,
        label: 'Refresh',
        onClick: () => { refetch(); refetchSummary(); },
        variant: 'icon',
      },
    ];
    if (allowedActions.includes('export')) {
      buttons.push({
        id: 'export',
        icon: <IconSystem.download size={16} />,
        label: exporting ? 'Exporting…' : 'Export',
        tooltip: 'Export to Excel',
        onClick: handleExport,
        variant: 'icon',
      });
      buttons.push({
        id: 'print',
        icon: <IconSystem.print size={16} />,
        label: printing ? 'Preparing…' : 'Print',
        tooltip: 'Print occupancy chart',
        onClick: handlePrint,
        variant: 'icon',
      });
    }
    return buttons;
  }, [refetch, refetchSummary, handleExport, handlePrint, exporting, printing, allowedActions]);

  const propertyLabel = fetchedPropertyLabel || propertyLabelsRef.current[propertyId] || '';
  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    { label: 'Night of', value: formatDate(chartDate), primary: true },
    ...(includeDepartures ? [{ label: 'Departures', value: 'Included', icon: 'calendar' as const }] : []),
    ...(enabled && summary ? [
      { label: 'Occupancy', value: `${summary.occupancyPercent}%  (${summary.occupiedRooms}/${summary.totalRooms})` },
      { label: 'Vacant', value: String(summary.vacantRooms ?? 0), title: 'Active rooms neither occupied, blocked nor out of order' },
      { label: 'Exp. arr / dep', value: `${summary.expectedArrivalRooms ?? 0} / ${summary.expectedDepartureRooms ?? 0}`, icon: 'calendar' as const,
        title: 'Rooms due in (not yet checked in) / checked-in rooms due out on the date' },
      { label: 'Pax', value: `${summary.totalPax}  (M ${summary.maleCount} · F ${summary.femaleCount} · C ${summary.childCount})` },
      { label: 'Resident / Foreign', value: `${summary.residentRooms} / ${summary.foreignerRooms}`, icon: 'users' as const },
      { label: 'ARR', value: formatCurrency(summary.arr), title: `Excl. complimentary ${formatCurrency(summary.arrExclComplimentary)}` },
      { label: 'RevPAR', value: formatCurrency(summary.revPar) },
      { label: 'RevPOR', value: formatCurrency(summary.revPor) },
    ] : []),
  ];

  const footer = enabled && summary ? (
    <>
      {summary.foodPlans?.length > 0 && (
        <span><b>Plans</b>&nbsp; {summary.foodPlans.map((f) => `${f.planCode} ${f.rooms}·${f.pax}`).join('   ')}</span>
      )}
      {(summary.settlements?.length ?? 0) > 0 && (
        <span><b>Settlement</b>&nbsp; {summary.settlements!.map((x) => `${settlementModeLabel(x.settlementMode) || x.settlementMode} ${x.rooms}`).join('   ')}</span>
      )}
      <span><b>Complimentary</b>&nbsp; {summary.complimentaryRooms}</span>
      <span><b>Blocked</b>&nbsp; {summary.blockedRooms}</span>
      <span><b>Out of order</b>&nbsp; {summary.oooRooms}</span>
      <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>Rooms · pax</span>
    </>
  ) : undefined;

  return (
    <EnterpriseScreenShell
      title="Room Occupancy Chart"
      description="Who is in which room on a night — plan, pax, settlement — with the house position."
      icon={<IconSystem.grid size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: appliedFilterCount }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label={<>Property <span className="text-danger">*</span></>}
                  value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')}
                  isAsync
                  loadOptions={loadProperties}
                  defaultOptions={true}
                  cacheOptions={false}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                  Chart Date <span className="text-danger">*</span>
                </label>
                <input type="date" className="form-input" value={chartDate}
                  onChange={(e) => setChartDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                  Rooms occupied on the NIGHT of this date.
                </div>
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeDepartures} onChange={(e) => setIncludeDepartures(e.target.checked)} />
                  Include today's departures
                </label>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                  Also list rooms due out on this date (morning view). Figures stay the night's.
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={footer}>
        {!enabled ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>
            Pick a property and a date in the filters.
          </div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>
            Failed to load — {error.message}
          </div>
        ) : (
          <SyutGrid
            data={data}
            columns={columns}
            enablePagination
            manualPagination
            totalCount={pagination?.totalItems ?? 0}
            currentPage={page - 1}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            enableSorting
            isLoading={loading}
            emptyMessage="No occupied rooms on the selected date."
          />
        )}
      </ReportBody>
    </EnterpriseScreenShell>
  );
};

export default OccupancyChartReportScreen;
